import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import useMediaQuery from "@mui/material/useMediaQuery";
import { IoClose } from "react-icons/io5";
import {
  MdCall,
  MdCallSplit,
  MdChecklist,
  MdContentCopy,
  MdDirectionsCar,
  MdDoneAll,
  MdFilterList,
  MdLocalShipping,
  MdLocationOff,
  MdLock,
  MdLockOpen,
  MdMyLocation,
  MdNavigation,
  MdOpenInNew,
  MdOutlineRoute,
  MdSwapVert,
} from "react-icons/md";

import CircularLoader from "../../../../../../../Components/CircularLoader/CircularLoader";
import PrintMenu from "../../../../../../../Components/PrintMenu/PrintMenu";
import BoltButton from "../../../../../../../Components/UI/Buttons/BoltButton";
import { readSession } from "../../../../../../../helpers/session";
import { useSnackBar } from "../../../../../../../hooks/useSnackBar";
import {
  fetchOrdersByIds,
  fetchSingleOrder,
} from "../../../../../../../services/orders.services";
import {
  addRunOrders,
  cancelRun,
  completeRun,
  fetchRun,
  fetchRunCandidates,
  fetchRunDrivers,
  lockRun,
  moveRunOrders,
  recordStopOutcome,
  removeRunOrders,
  reorderRunStops,
  sendDriverLocation,
  unlockRun,
  updateRun,
} from "../../../../../../../services/ShipperServices/DeliveryRoutes/deliveryRuns.services";
import CancelRunDialog from "./CancelRunDialog";
import CompleteRunDialog from "./CompleteRunDialog";
import { ChipDetails, InfoChip } from "./InfoChip";
import DeliveryList from "./DeliveryList";
import DriverView from "./DriverView";
import MoveStopsDialog from "./MoveStopsDialog";
import NewDriverForm, { NEW_DRIVER } from "./NewDriverForm";
import PickList from "./PickList";
import RouteSheetMap from "./RouteSheetMap";
import SheetChip from "./SheetChip";
import { mapStops } from "./routeMapStops";
import { StopCard, stopParts, StopRow } from "./StopParts";
import {
  byPostalCode,
  deliveryAddress,
  FULFILLED_STATUS,
  formatLb,
  orderPieces,
  orderWeight,
  pickList,
  readPicks,
  runLinks,
  sheetCounts,
  sheetLines,
  togglePick,
  writePicks,
} from "./routeSheet";
import useRoutePicks from "./useRoutePicks";
import {
  driverName,
  driverPhone,
  isLockedRun,
  isOpenRun,
  isUnderWayRefusal,
  LOCKED_HINT,
  lockedText,
  OUTCOME_FOR_KIND,
  recordedStops,
  RUN_STATUS,
  runDay,
  runClosing,
  runStatusText,
  runTitle,
} from "./runs";
import {
  bestStopOrder,
  reversedStopOrder,
  runStops as stopsToSort,
  SORT_PROBLEMS,
} from "./runOrder";
import {
  formatDuration,
  formatKm,
  loopPoints,
  routeTotals,
} from "./runDistance";
import {
  agoText,
  drivePlaces,
  driverSpot,
  metersText,
  nearestStop,
  readSharing,
  saveSharing,
  stopLocation,
  stopSpot,
  useDriverLocation,
} from "./driverLocation";

const PANEL =
  "overflow-hidden rounded-[7px] border-[1px] border-ui-border bg-ui-surface";
// The run's driver position as the server keeps it.
const driverFields = (run) => ({
  driver_lat: run?.driver_lat ?? null,
  driver_lng: run?.driver_lng ?? null,
  driver_accuracy: run?.driver_accuracy ?? null,
  driver_seen_at: run?.driver_seen_at ?? null,
});
// The office's sheet reads the driver's position again this often.
const WATCH_MS = 30000;
// Touch sizes (40px tall) on phones and tablets, and on touch screens of any
// size; from lg up with a mouse, the compact sizes as before.
const TAP = "min-h-[40px] lg:min-h-0 [@media(pointer:coarse)]:min-h-[40px]";
// BoltButton has its own 30px minimum, so its 40px comes from a media rule,
// which wins over it.
const TAP_BUTTON =
  "[@media(max-width:1023.98px)]:min-h-[40px] [@media(pointer:coarse)]:min-h-[40px]";
// Fields: as tall as TAP, and 16px text on phones so iPhones don't zoom in
// when one is tapped.
const FIELD_SIZE = `${TAP} text-[16px] sm:text-[12px]`;
const LINK_BTN = `flex items-center gap-[5px] whitespace-nowrap rounded-md border border-ui-border bg-ui-surface px-[10px] py-[5px] text-xs font-semibold text-ui-text-muted transition-colors hover:border-brand hover:bg-brand-light hover:text-brand ${TAP}`;
// Phones: buttons in a row share its width.
const STRETCH =
  "flex-[1_1_auto] justify-center md:flex-initial md:justify-start";

// Tailwind's md: from 768px up the stops are a table; below it (phones) each
// stop is a card. Without matchMedia (tests, very old browsers) it's the
// table, as before.
const WIDE = "(min-width:768px)";

// The pop-up's three views: where to go, what to take, and marking each stop.
const VIEW_LABEL = {
  sheet: "route sheet",
  pick: "pick list",
  delivery: "delivery list",
};
const VIEWS = [
  ["sheet", "Route sheet", MdOutlineRoute],
  ["pick", "Pick list", MdChecklist],
  ["delivery", "Delivery list", MdLocalShipping],
];

// A sheet takes up to 80 orders; they load in one request (fetchOrdersByIds),
// without their pallets' history: nothing on the sheet reads it, and on a
// busy product it's most of an order's size.
const MAX_ORDERS = 80;
const LOAD = { palletHistory: false };

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;
const telHref = (phone) => `tel:${String(phone).replace(/[^\d+]/g, "")}`;
// The delivery list's kind for each stop outcome; "fulfilled" (outside the
// run) only shows: the delivery list can still record the stop delivered.
const KIND_FOR_OUTCOME = {
  delivered: "delivered",
  partly: "partly",
  not_delivered: "cant",
  fulfilled: "fulfilled",
};
const errorText = (response, fallback) =>
  response.error?.response?.data?.message || fallback;

// A run's order ids, in its stop order.
const runOrderIds = (run) =>
  [...(run?.stops ?? [])]
    .sort((a, b) => Number(a?.position) - Number(b?.position))
    .map((stop) => stop.order_id);
const sameIds = (a, b) => {
  const left = a.map(String).sort();
  const right = b.map(String).sort();
  return (
    left.length === right.length &&
    left.every((id, index) => id === right[index])
  );
};
const CHANGED_ELSEWHERE =
  "This run changed since you opened it, so the sheet now shows its latest stops.";
const driveText = (totals) =>
  `${formatKm(totals.meters)} · ${formatDuration(totals.seconds)}`;
// An order's fulfilment status, and what the Orders list takes when it
// changed (as the delivery list's saves send it).
const statusOf = (order) =>
  String(order?.order_status_id ?? order?.fullfilment_status?.id ?? "");
const statusPatch = (order) => ({
  order_notes: order?.order_notes,
  fullfilment_status: order?.fullfilment_status,
  order_details: order?.order_details,
  order_status_id: order?.order_status_id,
});

/**
 * Route sheet for the self-delivery orders ticked on the Orders page: the
 * stops in delivery order (postal code first, ↑/↓ to change it), each with a
 * Navigate link; Google Maps links for the whole run (split every 9 stops,
 * Google's limit per link) starting and ending at the chosen location; the
 * drive's distance and time for the stops in their order (from Google
 * Directions: the embedded map's own box doesn't show on every route), also
 * in the summary lines and the printout; the road route on an embedded map;
 * Print.
 * From 768px up the stops are a table; on phones each stop is a card with
 * the same pieces and controls (see StopParts).
 * Each item shows its fulfilment state (fulfilled lines struck through, so
 * nobody packs them twice; lines not linked to a product are flagged) and,
 * as a rough guide, the product's current stock at the order's location; a
 * strip above the list adds them up. "Pick list" shows the same stops
 * product by product (see PickList); its Picked ticks and Missing reports
 * stay on this device, and a reported line shows "Missing" here too.
 * "Delivery list" marks each stop delivered, partly delivered or not
 * delivered, saving what the order view's status pop-up saves (see
 * DeliveryList); `canFulfil(order)` is the account's fulfil permission at the
 * order's location and `onOrderChanged(orderId, patch)` updates the Orders
 * list. The rest is not saved.
 * `statuses` is the app's fulfilment status list (to recognise Packing).
 * With `runId` the sheet shows a saved run instead of ticked orders: its
 * stops in the run's order, its date, driver (with Call) and status, and
 * changes save to the run (↑/↓, Best order, Reverse order, × on a stop not
 * marked yet, the start location, the date and driver, Lock, and Cancel run,
 * which asks first); the delivery list records each stop's outcome on the
 * run. Ticked stops not marked yet move to a new run or another open one
 * (Move stops). A locked run's stops can't change and it can't be cancelled
 * until it's unlocked; its stops can still be marked. A run that is no
 * longer open is read-only.
 * When the run changed elsewhere since the sheet loaded it (an order added or
 * taken off), the sheet follows it as soon as a save shows it; ↑/↓, Best
 * order and Reverse order, which the API then refuses, read the run again
 * first. After any refused change the sheet shows the run as it is now
 * (locked elsewhere, say).
 * `onRunChanged(run, removedOrderIds)` gets the run after each change; Move
 * stops also sends the run the stops joined. A run whose stops still to do
 * all have fulfilled orders (fulfilled from the order page) can be completed
 * in one go (Complete run). `onShowOrders(run)`, when given, adds "Show in
 * orders list" (the Orders page closes the sheet and filters by the run).
 * Each stop has the Orders list's fulfilment status dropdown (FulfilmentCell):
 * `onStatusChange(event, order)` opens what it opens there (the order view
 * with the status pop-up). When the order view closes the order is read
 * again, and a new status goes to `onOrderChanged`.
 * `canManageRuns` (the role's "Plan runs") allows changing the run: its
 * stops, date, driver, start, Lock and Cancel run; without it the run shows
 * as it is. `canDriveRuns` (the role's "Drive runs") allows recording stops
 * and Complete run. A store's own login has both.
 */
// Replaces a run's stop actions for a user whose role can't drive runs.
const NO_DRIVE_TEXT =
  "Your role doesn't include driving runs, so stops can't be marked here: ask your account's owner to add it in Settings → Roles.";

// What the location chip says when tapped, with the switch: the office sees
// where the driver is while the delivery list is open, and each stop
// recorded keeps where it was.
const LOCATION_TEXT = {
  off: "Location off: stops are recorded without where they were made.",
  waiting: "Finding your location…",
  on: "Location on: while this run is open here, the office sees where you are (and where you were last), and each stop you record keeps where it was made.",
  denied:
    "Location is blocked for this site in the browser's settings, so stops are recorded without it.",
  unavailable:
    "This browser can't share its location, so stops are recorded without it.",
};

// The location chip: a word or two and a tone for each state.
const LOCATION_LABEL = {
  off: "Location off",
  waiting: "Finding location",
  on: "Location on",
  denied: "Location blocked",
  unavailable: "No location",
};
const LOCATION_TONE = {
  off: "neutral",
  waiting: "neutral",
  on: "success",
  denied: "warning",
  unavailable: "warning",
};

const RouteSheetModal = ({
  open,
  orderIds = [],
  locations = [],
  defaultLocationId = null,
  statuses = [],
  canFulfil = () => false,
  onOrderChanged,
  onClose,
  runId = null,
  onRunChanged,
  onOpenOrder,
  orderViewOpen = false,
  onStatusChange,
  onShowOrders,
  canManageRuns = true,
  canDriveRuns = true,
}) => {
  const notify = useSnackBar();
  // Follows the window as it's resized or a phone is turned.
  const wide = useMediaQuery(WIDE, { defaultMatches: true });
  const closeRef = useRef(null);
  const [view, setView] = useState("sheet");
  const [picks, setPicks] = useState(readPicks);
  const [loading, setLoading] = useState(false);
  // Orders on the sheet that couldn't be loaded: { ids, cause } where cause is
  // "gone" (deleted, or not the user's to see) or "failed" (couldn't ask).
  const [missing, setMissing] = useState({ ids: [], cause: "gone" });
  const [stops, setStops] = useState([]);
  const stopsRef = useRef(stops);
  stopsRef.current = stops;
  const [startId, setStartId] = useState(null);
  // Saves running on the delivery list: while one runs the sheet can't be
  // closed or switched, as the status pop-up can't.
  const [saving, setSaving] = useState(0);
  const busy = saving > 0;
  const busyRef = useRef(busy);
  busyRef.current = busy;
  const markBusy = (on) =>
    setSaving((count) => Math.max(0, count + (on ? 1 : -1)));
  const tooMany = orderIds.length > MAX_ORDERS;
  const [run, setRun] = useState(null);
  // The run as last read, for saves that finish after the sheet redrew.
  const runRef = useRef(run);
  runRef.current = run;
  const [runFailed, setRunFailed] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [addingDriver, setAddingDriver] = useState(false);
  // The Cancel run pop-up: null, or { underWay, error }. Under way (stops
  // recorded), it waits for CANCEL to be typed.
  const [cancelling, setCancelling] = useState(null);
  // The Complete run pop-up: null, or { error }.
  const [completing, setCompleting] = useState(null);
  // The run chip whose details show (its id), or null.
  const [chipOpen, setChipOpen] = useState(null);
  // The route's orders that could join the run, for the map: { key,
  // orders } (key: the run and its stops when they were read).
  const [candidates, setCandidates] = useState({ key: "", orders: [] });
  const toggleChip = (id) =>
    setChipOpen((current) => (current === id ? null : id));
  const [sorting, setSorting] = useState(false);
  const [reversing, setReversing] = useState(false);
  // Stops ticked to move (order ids), and while Move stops is open, the
  // stops it moves (their orders, as ticked when it opened).
  const [ticked, setTicked] = useState([]);
  const [moving, setMoving] = useState(null);
  // The drive's distance and time for the stops in their order now:
  // { key, totals, state } (state: "idle", "working", "done" or "failed").
  const [drive, setDrive] = useState({ key: "", totals: null, state: "idle" });
  // Stops the delivery list saved on the order whose outcome the run couldn't
  // record ({ [orderId]: { outcome, reason } }): recorded again on their own,
  // without saving the order a second time.
  const [unrecorded, setUnrecorded] = useState({});
  const runOpen = isOpenRun(run);
  const runLocked = isLockedRun(run);
  // Whenever an open run is open on this device, whatever it shows, the
  // device shares its location (by default on phones and tablets only, see
  // readSharing): the office sees where the driver was last, and each stop
  // recorded keeps where it was and how far from the address.
  const [sharing, setSharing] = useState(readSharing);
  // The driver view (the run on the whole screen for the driver) is open.
  const [driverOpen, setDriverOpen] = useState(false);
  const tracking = open && Boolean(run?.id) && runOpen && sharing;
  const where = useDriverLocation({
    enabled: tracking,
    onPing: (fix) => {
      const runId = runRef.current?.id;
      if (!runId) return;
      sendDriverLocation(runId, fix).then((response) => {
        const seen = response.error ? null : response?.data?.result;
        if (seen?.id == null) return;
        setRun((current) =>
          current?.id === seen.id
            ? { ...current, ...driverFields(seen) }
            : current,
        );
      });
    },
  });
  const toggleSharing = () => {
    saveSharing(!sharing);
    setSharing(!sharing);
  };
  // The office's sheet follows the driver: while it's open on an open run
  // (and isn't the one sharing), the driver's position is read again every
  // WATCH_MS. Only those fields change, so nothing else on the sheet moves.
  const watchedRunId = open && runOpen && !tracking ? (run?.id ?? null) : null;
  useEffect(() => {
    if (watchedRunId == null) return undefined;
    const timer = setInterval(async () => {
      if (document.hidden) return;
      const response = await fetchRun(watchedRunId);
      const fresh = response.error ? null : response?.data?.result;
      if (fresh?.id !== watchedRunId) return;
      setRun((current) =>
        current?.id === fresh.id
          ? { ...current, ...driverFields(fresh) }
          : current,
      );
    }, WATCH_MS);
    return () => clearInterval(timer);
  }, [watchedRunId]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setMissing({ ids: [], cause: "gone" });
    setStops([]);
    setView("sheet");
    setPicks(readPicks());
    setRun(null);
    setRunFailed(false);
    setUnrecorded({});
    setCancelling(null);
    setCompleting(null);
    setChipOpen(null);
    setCandidates({ key: "", orders: [] });
    setDriverOpen(false);
    setTicked([]);
    setMoving(null);
    setAddingDriver(false);
    if (orderIds.length > MAX_ORDERS) {
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    // All the sheet's orders in one request.
    const loadOrders = (ids, savedRun) =>
      fetchOrdersByIds(ids, LOAD).then((result) => {
        if (cancelled) return;
        const { data: loaded, missing: notLoaded, error } = result;
        setMissing({ ids: notLoaded, cause: error ? "failed" : "gone" });
        // A run keeps its own stop order.
        setStops(savedRun ? loaded : [...loaded].sort(byPostalCode));
        // Start from the run's location, else the orders' own location when
        // those that have one share it.
        const shared = new Set(
          loaded
            .filter((order) => order?.warehouse_id != null)
            .map((order) => String(order.warehouse_id)),
        );
        const own = savedRun?.start_warehouse_id
          ? String(savedRun.start_warehouse_id)
          : shared.size === 1
            ? [...shared][0]
            : null;
        const known = (id) =>
          locations.some((location) => String(location.id) === String(id));
        setStartId(
          own && known(own)
            ? own
            : known(defaultLocationId)
              ? String(defaultLocationId)
              : locations[0]
                ? String(locations[0].id)
                : null,
        );
        setLoading(false);
      });
    if (runId) {
      fetchRunDrivers().then((response) => {
        const list = response.error ? null : response?.data?.result;
        if (!cancelled && Array.isArray(list)) setDrivers(list);
      });
      fetchRun(runId).then((response) => {
        if (cancelled) return;
        const savedRun = response.error ? null : response?.data?.result;
        if (savedRun?.id == null) {
          setRunFailed(true);
          setLoading(false);
          return;
        }
        setRun(savedRun);
        loadOrders(
          [...(savedRun.stops ?? [])]
            .sort((a, b) => a.position - b.position)
            .map((stop) => stop.order_id),
          savedRun,
        );
      });
    } else {
      loadOrders(orderIds, null);
    }
    return () => {
      cancelled = true;
    };
    // Loaded once per opening; the ticked orders don't change while it's open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Focus moves to Close once per opening; Esc uses the latest onClose and
  // waits while a save runs. While the order view is open on top, Esc is its.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const viewingRef = useRef(orderViewOpen);
  viewingRef.current = orderViewOpen;
  useEffect(() => {
    if (!open) return undefined;
    closeRef.current?.focus();
    const onKey = (event) => {
      if (event.key === "Escape" && !busyRef.current && !viewingRef.current)
        onCloseRef.current();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const start = locations.find(
    (location) => String(location.id) === String(startId),
  );
  const routable = useMemo(
    () =>
      stops
        .map((order) => ({
          order,
          address: deliveryAddress(order?.order_shipping_address_details),
        }))
        .filter((stop) => stop.address),
    [stops],
  );
  const addresses = routable.map((stop) => stop.address);
  const links = runLinks(start?.address || null, addresses, start?.name);

  // The drive through the stops in their order (from the start and back),
  // asked again whenever the stops or the start change. Google's embedded
  // map only sometimes shows its own distance and time box.
  const drivePoints = loopPoints(start?.address || null, addresses);
  const driveKey = drivePoints.join("\n");
  const canDrive =
    open &&
    !loading &&
    drivePoints.length >= 2 &&
    Boolean(window.google?.maps?.DirectionsService);
  useEffect(() => {
    if (!canDrive) {
      setDrive({ key: "", totals: null, state: "idle" });
      return undefined;
    }
    let cancelled = false;
    setDrive((current) =>
      current.key === driveKey && current.state !== "idle"
        ? current
        : { key: driveKey, totals: null, state: "working" },
    );
    routeTotals(drivePoints).then((totals) => {
      if (cancelled) return;
      setDrive({
        key: driveKey,
        totals,
        state: totals ? "done" : "failed",
      });
    });
    return () => {
      cancelled = true;
    };
    // The points are the key's parts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canDrive, driveKey]);
  // Where Google put each stop's address on that drive (the same request,
  // asked once): for how far a recorded stop was from it, and the stop the
  // driver is nearest.
  const [places, setPlaces] = useState(() => new Map());
  useEffect(() => {
    if (!canDrive) return undefined;
    let cancelled = false;
    drivePlaces(drivePoints).then((found) => {
      if (!cancelled) setPlaces(found);
    });
    return () => {
      cancelled = true;
    };
    // The points are the key's parts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canDrive, driveKey]);
  const driveState = !canDrive
    ? "idle"
    : drive.key === driveKey
      ? drive.state
      : "working";
  const driveTotals = driveState === "done" ? drive.totals : null;
  const noAddress = stops.filter(
    (order) => !deliveryAddress(order?.order_shipping_address_details),
  );

  const pieces = stops.reduce((sum, order) => sum + orderPieces(order), 0);
  const weights = stops.map(orderWeight);
  const weight = weights.every((value) => value != null)
    ? weights.reduce((sum, value) => sum + value, 0)
    : null;
  const routeNames = new Set(stops.map((order) => order?.route?.name ?? ""));
  const routeName = routeNames.size === 1 ? [...routeNames][0] : "";
  const summary = [
    plural(stops.length, "stop"),
    `${pieces} pcs`,
    weight != null && formatLb(weight),
    driveTotals && driveText(driveTotals),
  ]
    .filter(Boolean)
    .join(" · ");
  const checked = useMemo(() => sheetLines(stops, statuses), [stops, statuses]);

  // The PDF of the tab on show, in the version and language picked (see
  // print/makeDocument, which loads on the first print).
  const makePrint = useCallback(
    async ({ level, lang }) => {
      const { makeRouteDocument } = await import("./print/makeDocument");
      const user = readSession().user ?? {};
      return makeRouteDocument({
        view,
        level,
        lang,
        stops,
        lines: checked,
        run,
        start: start ? { name: start.name, address: start.address } : null,
        routeName,
        drive: driveTotals,
        printedBy:
          [user.firstname, user.lastname].filter(Boolean).join(" ") ||
          user.username ||
          "",
      });
    },
    [view, stops, checked, run, start, routeName, driveTotals],
  );
  const counts = sheetCounts(checked);
  const list = useMemo(() => pickList(stops, checked), [stops, checked]);
  const markPick = (row, mark) =>
    setPicks((current) => {
      const next = togglePick(current, row.lineIds, mark);
      writePicks(next);
      return next;
    });

  // What is picked, from Bolt (the same picks as the warehouse app), for
  // the Delivery list's tick boxes and the Pick list's Picked ticks. Where
  // Bolt has no picks, the pick list keeps its ticks on this device.
  const routePicks = useRoutePicks(
    stops,
    view === "delivery" || view === "pick" || driverOpen,
  );
  const lineOrders = useMemo(() => {
    const byLine = new Map();
    stops.forEach((order) =>
      (order?.order_details ?? []).forEach((item) =>
        byLine.set(String(item?.id), { order, item }),
      ),
    );
    return byLine;
  }, [stops]);
  // What the Pick list shows ticked: Bolt's picks; lines Bolt can't pick
  // (not in Products) keep their tick on this device. `busy`: lines being
  // saved, whose tick waits.
  const shownPicks = useMemo(() => {
    if (!routePicks.ready) return picks;
    const picked = new Set();
    lineOrders.forEach((_, lineId) => {
      if (
        routePicks.lineOf(lineId)
          ? routePicks.isPicked(lineId)
          : picks.picked.has(lineId)
      ) {
        picked.add(lineId);
      }
    });
    return { picked, missing: picks.missing, busy: routePicks.busy };
  }, [routePicks, lineOrders, picks]);
  // The Delivery list's tick boxes: Bolt's picks; where Bolt has none (no
  // Bolt Locations on this server, or this login can't see picks), the
  // ticks this device keeps, the same as the Pick list's.
  const deliveryPicks =
    routePicks.status === "unavailable"
      ? {
          ready: true,
          device: true,
          lineOf: (lineId) =>
            lineOrders.has(String(lineId)) ? { picks: [] } : undefined,
          isPicked: (lineId) => picks.picked.has(String(lineId)),
          isBusy: () => false,
          toggle: (order, item) =>
            markPick({ lineIds: [String(item?.id)] }, "picked"),
        }
      : routePicks;
  // A tick made while Bolt is still being asked waits for its answer, then
  // goes to Bolt or, where Bolt has no picks, stays on this device.
  const waitingTicks = useRef([]);
  // A Pick list row's lines: those Bolt picks ({ order, item }), and the
  // ids of those it can't (not in Products).
  const rowLines = (row) => {
    const ids = row.lineIds.map(String);
    const entries = ids
      .map((lineId) => lineOrders.get(lineId))
      .filter((entry) => entry && routePicks.lineOf(entry.item?.id));
    const inBolt = new Set(entries.map((entry) => String(entry.item?.id)));
    return { ids, entries, onDevice: ids.filter((id) => !inBolt.has(id)) };
  };
  const markPicked = (row) => {
    if (routePicks.status === "loading") {
      waitingTicks.current.push(row);
      return;
    }
    if (!routePicks.ready) return markPick(row, "picked");
    const { ids, entries, onDevice } = rowLines(row);
    // A line still being saved: this tap waits for it rather than send it
    // again.
    if (ids.some((id) => routePicks.busy.has(id))) return;
    const pick = !ids.every((id) => shownPicks.picked.has(id));
    if (entries.length) routePicks.pickLines(entries, pick);
    // Lines Bolt can't pick keep their tick here; a pick clears Missing.
    const clears = pick && ids.some((id) => picks.missing.has(id));
    if (!onDevice.length && !clears) return;
    setPicks((current) => {
      const next = {
        picked: new Set(current.picked),
        missing: new Set(current.missing),
      };
      onDevice.forEach((id) =>
        pick ? next.picked.add(id) : next.picked.delete(id),
      );
      if (pick) ids.forEach((id) => next.missing.delete(id));
      writePicks(next);
      return next;
    });
  };
  // Missing is kept on this device. Reporting it undoes the line's picks:
  // in Bolt too, where Bolt has them.
  const markMissing = (row) => {
    const { ids, entries } = rowLines(row);
    const reporting = !ids.every((id) => picks.missing.has(id));
    markPick(row, "missing");
    if (reporting && routePicks.ready) {
      const picked = entries.filter(
        (entry) =>
          routePicks.isPicked(entry.item?.id) &&
          !routePicks.busy.has(String(entry.item?.id)),
      );
      if (picked.length) routePicks.pickLines(picked, false);
    }
  };
  useEffect(() => {
    if (routePicks.status === "loading" || !waitingTicks.current.length) return;
    const rows = waitingTicks.current;
    waitingTicks.current = [];
    rows.forEach(markPicked);
  }, [routePicks.status]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!routePicks.message) return;
    notify.error(routePicks.message);
    routePicks.clearMessage();
  }, [routePicks.message]); // eslint-disable-line react-hooks/exhaustive-deps

  // A stop's status changed on the delivery list: show it straight away,
  // reload the order for its full detail, and tell the Orders page.
  const handleChanged = (orderId, patch) => {
    const known = Object.fromEntries(
      Object.entries(patch).filter(([, value]) => value !== undefined),
    );
    const replace = (order) =>
      setStops((current) =>
        current.map((stop) => (stop.id === orderId ? order(stop) : stop)),
      );
    replace((stop) => ({ ...stop, ...known }));
    onOrderChanged?.(orderId, patch);
    // Fulfilling places the picks made with no spot: read them again.
    routePicks.reload([orderId]);
    fetchSingleOrder(orderId).then((response) => {
      const fresh = response.error ? null : response?.data?.result;
      if (fresh?.id != null) replace(() => fresh);
    });
  };

  // The delivery list read a stop's order again before marking it: keep it.
  const handleFresh = (order) =>
    setStops((current) =>
      current.map((stop) => (stop.id === order.id ? order : stop)),
    );

  // An order number opens the order view on top of the sheet (the Orders page
  // owns it). When it closes, that stop's order is read again, so a change
  // made there (its status, say) shows here too.
  const viewedRef = useRef(null);
  const openOrder = onOpenOrder
    ? (order) => {
        viewedRef.current = order.id;
        onOpenOrder(order);
      }
    : null;
  // A stop's status dropdown does what the Orders list's does: the order view
  // opens with the status pop-up, on top of the sheet. It's read again after.
  const changeStatus = onStatusChange
    ? (event, order) => {
        viewedRef.current = order.id;
        onStatusChange(event, order);
      }
    : null;
  useEffect(() => {
    if (orderViewOpen || viewedRef.current == null) return undefined;
    const id = viewedRef.current;
    viewedRef.current = null;
    let cancelled = false;
    fetchSingleOrder(id).then((response) => {
      const fresh = response.error ? null : response?.data?.result;
      if (cancelled || fresh?.id == null) return;
      const shown = stopsRef.current.find((stop) => stop.id === fresh.id);
      handleFresh(fresh);
      // A new status goes to the Orders list, as the delivery list's do.
      if (shown && statusOf(shown) !== statusOf(fresh)) {
        onOrderChanged?.(fresh.id, statusPatch(fresh));
      }
    });
    return () => {
      cancelled = true;
    };
    // Only when the order view closes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderViewOpen]);

  const moveStop = (index, delta) =>
    setStops((current) => {
      const next = [...current];
      const [stop] = next.splice(index, 1);
      next.splice(index + delta, 0, stop);
      return next;
    });

  // `fresh` is the run as the API has it now. When its stops aren't the ones
  // the sheet had (`before`, less `removed`: those were taken off here),
  // someone changed the run elsewhere since the sheet loaded it: the sheet
  // follows it (keeping the orders it has, reading the new ones, in the run's
  // order) and tells the Orders page. Resolves to true when that happened.
  const followRun = async (fresh, before, removed = []) => {
    const ids = runOrderIds(fresh);
    const had = runOrderIds(before).filter(
      (id) => !removed.some((off) => String(off) === String(id)),
    );
    if (sameIds(ids, had)) return false;
    const gone = runOrderIds(before).filter(
      (id) => !ids.some((kept) => String(kept) === String(id)),
    );
    const loaded = new Set(stopsRef.current.map((order) => String(order.id)));
    const { data: read, error } = await fetchOrdersByIds(
      ids.filter((id) => !loaded.has(String(id))),
      LOAD,
    );
    read.forEach((order) => loaded.add(String(order.id)));
    const follow = (current) => {
      const byId = new Map(current.map((order) => [String(order.id), order]));
      read.forEach((order) => byId.set(String(order.id), order));
      return ids.map((id) => byId.get(String(id))).filter(Boolean);
    };
    // Straight away for what runs next (Best order's totals), before the
    // sheet draws them.
    stopsRef.current = follow(stopsRef.current);
    setStops(follow);
    setMissing({
      ids: ids.filter((id) => !loaded.has(String(id))),
      cause: error ? "failed" : "gone",
    });
    onRunChanged?.(fresh, gone);
    return true;
  };

  // The run read again after the API refused a change, so the sheet shows it
  // as it is now (locked elsewhere, say). Resolves to it, or null.
  const refreshRun = async () => {
    const before = runRef.current;
    if (before?.id == null) return null;
    const response = await fetchRun(before.id);
    const fresh = response.error ? null : response?.data?.result;
    if (fresh?.id == null) return null;
    setRun(fresh);
    if (await followRun(fresh, before)) notify.info(CHANGED_ELSEWHERE);
    return fresh;
  };

  // Each change to a run saves straight away; while it saves the sheet waits.
  // The run the API sends back can show a change made elsewhere: the sheet
  // follows it. A refused change says why (`onRefused(response)` can say it
  // itself by returning true) and reads the run again.
  const saveRun = async (request, fallback, removed = [], onRefused) => {
    markBusy(true);
    try {
      const response = await request();
      const saved = response.error ? null : response?.data?.result;
      if (saved?.id == null) {
        if (!onRefused?.(response)) notify.error(errorText(response, fallback));
        await refreshRun();
        return null;
      }
      const before = runRef.current;
      setRun(saved);
      onRunChanged?.(saved, removed);
      if (await followRun(saved, before, removed)) {
        notify.info(CHANGED_ELSEWHERE);
      }
      return saved;
    } finally {
      markBusy(false);
    }
  };

  // A new stop order. The API refuses one that doesn't list the run's stops
  // as they are now, as when someone added or took off an order elsewhere
  // since the sheet loaded the run: then the run is read again and the sheet
  // follows it. Resolves to { saved }, { changed } (the run as it is now), or
  // {} when it was refused for another reason (and says why).
  const saveOrder = async (ids, fallback) => {
    markBusy(true);
    try {
      const response = await reorderRunStops(run.id, ids);
      const saved = response.error ? null : response?.data?.result;
      if (saved?.id != null) {
        setRun(saved);
        onRunChanged?.(saved, []);
        return { saved };
      }
      const again = await fetchRun(run.id);
      const fresh = again.error ? null : again?.data?.result;
      if (fresh?.id != null) {
        const changed = await followRun(fresh, runRef.current);
        // As it is now, also when only its lock or outcomes changed.
        setRun(fresh);
        if (changed) return { changed: fresh };
      }
      notify.error(errorText(response, fallback));
      return {};
    } finally {
      markBusy(false);
    }
  };

  const move = async (index, delta) => {
    if (!run) {
      moveStop(index, delta);
      return;
    }
    // Swap the two stops in the run's full list (it may hold an order that
    // couldn't be loaded here).
    const ids = runOrderIds(run);
    const from = ids.indexOf(stops[index].id);
    const to = ids.indexOf(stops[index + delta].id);
    [ids[from], ids[to]] = [ids[to], ids[from]];
    moveStop(index, delta);
    const result = await saveOrder(
      ids,
      "Couldn't save the new order. Try again.",
    );
    // A run that changed elsewhere now shows as it is: the move is redone there.
    if (result.changed) {
      notify.warning(`${CHANGED_ELSEWHERE} Move the stop again.`);
    } else if (!result.saved) {
      moveStop(index + delta, -delta);
    }
  };

  // The drive for the stops in two orders: [before, after] totals, each null
  // when Google can't say. Addresses as the map and the drive line have them
  // (from the orders on the sheet).
  const compareDrives = (beforeIds, afterIds) => {
    const addressOf = new Map(
      stopsRef.current.map((order) => [
        String(order.id),
        deliveryAddress(order?.order_shipping_address_details),
      ]),
    );
    const addressesFor = (ids) =>
      ids.map((id) => addressOf.get(String(id))).filter(Boolean);
    const from = start?.address || null;
    return Promise.all([
      routeTotals(loopPoints(from, addressesFor(beforeIds))),
      routeTotals(loopPoints(from, addressesFor(afterIds))),
    ]);
  };

  // Best order: the run's stops still to deliver, sorted for the shortest
  // drive from the start and back (marked stops stay first), saved straight
  // away like a move. Google's order is kept only when it's quicker (a
  // minute or more), or about as quick and shorter (0.5 km or more): an
  // order that gains nothing isn't worth the change. A run that changed
  // elsewhere is read again and its latest stops are sorted instead.
  const sortStops = async () => {
    if (!run) return;
    setSorting(true);
    markBusy(true);
    try {
      let current = run;
      for (let attempt = 0; attempt < 2; attempt += 1) {
        const lead = attempt ? "The run had changed since you opened it. " : "";
        const already = attempt
          ? `${lead}Its latest stops are already in the best order.`
          : "The stops are already in the best order.";
        // One attempt at a time: the second sorts the run as it is now.
        // eslint-disable-next-line no-await-in-loop
        const best = await bestStopOrder(
          start?.address || "",
          stopsToSort(current),
        );
        if (best.problem) {
          notify.error(`${lead}${SORT_PROBLEMS[best.problem]}.`);
          return;
        }
        if (!best.changed) {
          notify.success(already);
          return;
        }
        // eslint-disable-next-line no-await-in-loop
        const [was, now] = await compareDrives(runOrderIds(current), best.ids);
        const gains =
          !was ||
          !now ||
          now.seconds <= was.seconds - 60 ||
          (now.seconds < was.seconds + 60 && now.meters <= was.meters - 500);
        if (!gains) {
          notify.success(already);
          return;
        }
        // eslint-disable-next-line no-await-in-loop
        const result = await saveOrder(
          best.ids,
          "Couldn't save the new order. Try again.",
        );
        if (result.saved) {
          const at = new Map(best.ids.map((id, index) => [String(id), index]));
          const place = (order) =>
            at.get(String(order.id)) ?? Number.MAX_SAFE_INTEGER;
          setStops((stopsNow) =>
            [...stopsNow].sort((a, b) => place(a) - place(b)),
          );
          const totals =
            was && now
              ? `: ${formatKm(was.meters)} → ${formatKm(now.meters)}, ${formatDuration(was.seconds)} → ${formatDuration(now.seconds)}`
              : "";
          notify.success(
            attempt
              ? `${lead}Its latest stops are now sorted for the shortest drive${totals}.`
              : `Stops sorted for the shortest drive${totals}.`,
          );
          return;
        }
        if (!result.changed) return;
        current = result.changed;
      }
      notify.warning(
        "This run keeps changing. Close the sheet and open it again.",
      );
    } finally {
      markBusy(false);
      setSorting(false);
    }
  };

  // Reverse order: the stops still to deliver the other way round (marked
  // stops stay first, as Best order keeps them), saved straight away like a
  // move. A run that changed elsewhere is read again and its latest stops
  // are reversed instead.
  const reverseStops = async () => {
    if (!run) return;
    setReversing(true);
    markBusy(true);
    try {
      let current = run;
      for (let attempt = 0; attempt < 2; attempt += 1) {
        const lead = attempt ? "The run had changed since you opened it. " : "";
        const reversed = reversedStopOrder(current);
        if (!reversed.changed) {
          notify.info(
            `${lead}Fewer than 2 stops are still to deliver: nothing to reverse.`,
          );
          return;
        }
        // One attempt at a time: the second reverses the run as it is now.
        // eslint-disable-next-line no-await-in-loop
        const result = await saveOrder(
          reversed.ids,
          "Couldn't save the new order. Try again.",
        );
        if (result.saved) {
          const at = new Map(
            reversed.ids.map((id, index) => [String(id), index]),
          );
          const place = (order) =>
            at.get(String(order.id)) ?? Number.MAX_SAFE_INTEGER;
          setStops((stopsNow) =>
            [...stopsNow].sort((a, b) => place(a) - place(b)),
          );
          notify.success(
            attempt
              ? `${lead}Its latest stops are now reversed.`
              : "Stops reversed.",
          );
          return;
        }
        if (!result.changed) return;
        current = result.changed;
      }
      notify.warning(
        "This run keeps changing. Close the sheet and open it again.",
      );
    } finally {
      markBusy(false);
      setReversing(false);
    }
  };

  const removeStop = async (orderId) => {
    const saved = await saveRun(
      () => removeRunOrders(run.id, [orderId]),
      "Couldn't take the order off the run. Try again.",
      [orderId],
    );
    if (saved) {
      setStops((current) => current.filter((stop) => stop.id !== orderId));
    }
  };

  // An order added from the map: it joins the end of the run, and the
  // sheet loads it.
  const addStop = async (orderId) => {
    const before = runRef.current;
    markBusy(true);
    try {
      const response = await addRunOrders(run.id, [orderId]);
      const saved = response.error ? null : response?.data?.result;
      if (saved?.id == null) {
        notify.error(
          errorText(response, "Couldn't add the order to the run. Try again."),
        );
        await refreshRun();
        return;
      }
      setRun(saved);
      await followRun(saved, before);
      notify.success("Added to the run.");
    } finally {
      markBusy(false);
    }
  };

  const changeRun = (changes, fallback) =>
    saveRun(() => updateRun(run.id, changes), fallback);

  const changeStart = (id) => {
    setStartId(id);
    if (run && runOpen) {
      changeRun(
        { start_warehouse_id: id ? Number(id) : null },
        "Couldn't save the start location. Try again.",
      );
    }
  };

  // Lock: the run's stops stay as they are (none added, taken off, moved or
  // reordered) and it can't be cancelled until it's unlocked; the delivery
  // list still records each stop.
  const toggleLock = async () => {
    const locking = !isLockedRun(run);
    const saved = await saveRun(
      () => (locking ? lockRun(run.id) : unlockRun(run.id)),
      locking
        ? "Couldn't lock the run. Try again."
        : "Couldn't unlock the run. Try again.",
    );
    if (saved && locking) setTicked([]);
  };

  // Cancel run asks first; a run under way (stops recorded) waits for CANCEL
  // to be typed and is cancelled with confirm_under_way.
  const openCancel = () =>
    setCancelling({ underWay: recordedStops(run) > 0, error: "" });
  // When the API says the run is under way after all (stops recorded
  // elsewhere), the pop-up asks for CANCEL, with the run read again for how
  // many; any other refusal says why in the pop-up.
  const confirmCancel = async () => {
    const underWay = Boolean(cancelling?.underWay) || recordedStops(run) > 0;
    let refusal = {};
    const saved = await saveRun(
      () => cancelRun(run.id, { confirmUnderWay: underWay }),
      "Couldn't cancel the run. Try again.",
      [],
      (response) => {
        refusal = response;
        return true;
      },
    );
    if (saved) {
      setCancelling(null);
      return;
    }
    setCancelling(
      (current) =>
        current &&
        (!underWay && isUnderWayRefusal(refusal)
          ? { underWay: true, error: "" }
          : {
              ...current,
              error: errorText(refusal, "Couldn't cancel the run. Try again."),
            }),
    );
  };

  // Complete run / Close run: the stops still to do are recorded from their
  // orders (fulfilled from the order page, so the run wasn't told) and the
  // run closes: delivered, partly delivered, or (Close run) not delivered. A
  // refusal says why in the pop-up.
  const confirmComplete = async () => {
    let refusal = {};
    const unfinished = !closing?.allFulfilled;
    const saved = await saveRun(
      () => completeRun(run.id, { closeUnfinished: unfinished }),
      "Couldn't close the run. Try again.",
      [],
      (response) => {
        refusal = response;
        return true;
      },
    );
    if (saved) {
      setCompleting(null);
      return;
    }
    setCompleting(
      (current) =>
        current && {
          error: errorText(refusal, "Couldn't close the run. Try again."),
        },
    );
  };

  // Where the phone is as a stop is recorded, and how far that is from the
  // stop's address on the map; null when it isn't sharing or has no fix.
  const locateStop = async (orderId) => {
    if (!tracking) return null;
    const fix = await where.getFix();
    if (!fix) return null;
    const order = stopsRef.current.find((stop) => stop.id === orderId);
    const address = deliveryAddress(order?.order_shipping_address_details);
    return stopLocation(fix, address ? places.get(address) : null);
  };

  // Records a stop's outcome on the run, with where it happened when this
  // device shares its location; one that fails waits in `unrecorded` (with
  // that location) for Record on the run.
  const recordOutcome = async (orderId, outcome, reason, known) => {
    const location = known !== undefined ? known : await locateStop(orderId);
    const saved = await saveRun(
      () => recordStopOutcome(run.id, orderId, outcome, reason, location),
      "The order is saved, but the run couldn't record it. Use Record on the run.",
    );
    setUnrecorded((current) => {
      const next = { ...current };
      if (saved) delete next[orderId];
      else next[orderId] = { outcome, reason, location };
      return next;
    });
    return saved;
  };

  // The delivery list saved a stop: record its outcome on the run too.
  const handleOutcome = (orderId, kind, reason) => {
    const outcome = OUTCOME_FOR_KIND[kind];
    if (!run || !runOpen || !outcome) return;
    recordOutcome(orderId, outcome, reason);
  };

  // Record on the run: only the run's outcome is sent again, one stop at a
  // time; the orders were saved already.
  const recordAgain = async () => {
    for (const [orderId, { outcome, reason, location }] of Object.entries(
      unrecorded,
    )) {
      if (
        // eslint-disable-next-line no-await-in-loop
        !(await recordOutcome(
          Number(orderId),
          outcome,
          reason,
          location ?? null,
        ))
      )
        return;
    }
  };
  const unrecordedCount = Object.keys(unrecorded).length;

  const runStops = Object.fromEntries(
    (run?.stops ?? []).map((stop) => [stop.order_id, stop]),
  );
  const runOutcomes = Object.fromEntries(
    (run?.stops ?? [])
      .filter((stop) => KIND_FOR_OUTCOME[stop.outcome])
      .map((stop) => [
        stop.order_id,
        {
          kind: KIND_FOR_OUTCOME[stop.outcome],
          reason: stop.outcome_reason ?? "",
        },
      ]),
  );
  // Where each recorded stop was (order id → its spot), for the sheet, the
  // delivery list and the map; and where the driver was last seen, with the
  // stop nearest them.
  const spots = Object.fromEntries(
    (run?.stops ?? [])
      .map((stop) => [stop.order_id, stopSpot(stop)])
      .filter(([, spot]) => spot),
  );
  const driver = driverSpot(run);
  const driverNear = driver
    ? nearestStop(driver.place, mapStops(stops), places)
    : null;
  const canEditRun = Boolean(run) && runOpen && canManageRuns;
  // Complete run records the stops still to do, as a driver would.
  const canCompleteRun = Boolean(run) && runOpen && canDriveRuns;
  // On the map, an open, unlocked run's stops not marked yet can come off
  // it, and the orders on its route that could join it show (read when the
  // route sheet shows, again as its stops change).
  const canChangeStops = canEditRun && !runLocked;
  const removableIds = canChangeStops
    ? (run.stops ?? [])
        .filter((stop) => stop.outcome === "pending")
        .map((stop) => stop.order_id)
    : [];
  const routeId = run?.route_id ?? run?.route?.id ?? null;
  const candidatesKey =
    open && canChangeStops && routeId != null
      ? `${run.id}:${runOrderIds(run).join(",")}`
      : "";
  const wantCandidates = Boolean(candidatesKey) && view === "sheet";
  useEffect(() => {
    if (!wantCandidates || candidates.key === candidatesKey) return undefined;
    let cancelled = false;
    (async () => {
      const response = await fetchRunCandidates(run.id);
      const ids = response?.error
        ? []
        : (response?.data?.result?.order_ids ?? []);
      const read = ids.length ? await fetchOrdersByIds(ids, LOAD) : null;
      if (!cancelled) {
        setCandidates({ key: candidatesKey, orders: read?.data ?? [] });
      }
    })();
    return () => {
      cancelled = true;
    };
    // The key says when to read them again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantCandidates, candidatesKey]);
  const mapCandidates =
    candidatesKey && candidates.key === candidatesKey ? candidates.orders : [];
  // Closing the run from its orders (fulfilled from the order page):
  // Complete run when every order still to deliver is fulfilled, else Close
  // run (null when nothing was delivered yet).
  const closing = runClosing(run, stops);
  // Of the orders the sheet couldn't load (deleted, or not the user's to
  // see), those whose stops still wait can come off the run, so it can be
  // completed. A stop that has its outcome stays, as the run's record.
  const removableMissing =
    canEditRun && missing.cause === "gone"
      ? missing.ids.filter((id) => runStops[id]?.outcome === "pending")
      : [];
  const takeOffMissing = async () => {
    const ids = removableMissing;
    const saved = await saveRun(
      () => removeRunOrders(run.id, ids),
      `Couldn't take ${ids.length === 1 ? "that order" : "those orders"} off the run. Try again.`,
      ids,
    );
    if (saved) {
      setMissing((current) => ({
        ...current,
        ids: current.ids.filter(
          (id) => !ids.some((off) => String(off) === String(id)),
        ),
      }));
    }
  };

  // Stops ticked to move: those still waiting for their outcome, on an open
  // run that isn't locked.
  const canMove = (orderId) =>
    canEditRun && !runLocked && runStops[orderId]?.outcome === "pending";
  const movableIds = stops.map((order) => order.id).filter(canMove);
  const tickedIds = ticked.filter((id) => movableIds.includes(id));
  const tickedOrders = stops.filter((order) => tickedIds.includes(order.id));
  const allTicked =
    movableIds.length > 0 && tickedIds.length === movableIds.length;
  const toggleTick = (orderId) =>
    setTicked((current) =>
      current.includes(orderId)
        ? current.filter((id) => id !== orderId)
        : [...current, orderId],
    );
  // Ticks every stop still to deliver, or none: in the table's heading, and
  // above the cards on phones.
  const tickAllBox = (className) => (
    <input
      type="checkbox"
      aria-label="Tick every stop still to deliver"
      title={runLocked ? LOCKED_HINT : "Tick stops to move them to another run"}
      checked={allTicked}
      disabled={busy || !movableIds.length}
      onChange={() => setTicked(allTicked ? [] : movableIds)}
      className={className}
    />
  );
  // What every stop's row or card shares from the sheet (see StopParts):
  // both take the same handlers and rules.
  const stopSheet = {
    isRun: Boolean(run),
    canEditRun,
    locked: runLocked,
    busy,
    count: stops.length,
    ticked: tickedIds,
    canMove,
    removable: (orderId) =>
      canEditRun && runStops[orderId]?.outcome === "pending",
    onTick: toggleTick,
    onMove: move,
    onRemove: removeStop,
    onOpen: openOrder,
    onStatusChange: changeStatus,
    statuses,
    canFulfil,
    missing: picks.missing,
    formatLb,
    spots,
  };

  // Move stops: the ticked stops leave this run for a new run or the end of
  // another open one. The sheet shows this run without them, and the Orders
  // list hears about both runs, as after Take off and Add to run. Resolves to
  // why they couldn't move, or "".
  const moveTicked = async (target) => {
    const ids = (moving ?? []).map((order) => order.id);
    markBusy(true);
    try {
      const response = await moveRunOrders(run.id, ids, target);
      const result = response.error ? null : response?.data?.result;
      if (result?.from?.id == null) {
        // It may have been locked or changed elsewhere: show it as it is.
        await refreshRun();
        return errorText(response, "Couldn't move the stops. Try again.");
      }
      const { from, to } = result;
      const left = new Set(runOrderIds(from).map(String));
      const moved = ids.filter((id) => !left.has(String(id)));
      const before = runRef.current;
      setRun(from);
      setStops((current) =>
        current.filter((order) => !moved.includes(order.id)),
      );
      setTicked([]);
      setMoving(null);
      onRunChanged?.(from, moved);
      // The moved orders now show the run they joined.
      if (to?.id != null) onRunChanged?.(to, []);
      if (await followRun(from, before, moved)) notify.info(CHANGED_ELSEWHERE);
      if (moved.length) {
        notify.success(
          `${plural(moved.length, "stop")} moved to ${runTitle(to)}.`,
        );
      } else {
        notify.warning("No stops moved: each has its outcome now.");
      }
      return "";
    } finally {
      markBusy(false);
    }
  };
  const [runStatusLabel, runStatusTone] = RUN_STATUS[run?.status] ?? [
    run?.status ?? "",
    "neutral",
  ];
  // The run's chips, a word or two each; tapped, one shows the rest under
  // them. Location only where it's shared (an open run's delivery list).
  const locationState = sharing ? where.state : "off";
  const nearText = driverNear
    ? `${metersText(driverNear.meters)} from stop ${driverNear.stop.number}${driverNear.stop.customer ? ` (${driverNear.stop.customer})` : ""}`
    : "";
  const lockText = run
    ? `${lockedText(run)}. Its stops can't be added, taken off, moved or reordered, and it can't be cancelled, until it's unlocked. The delivery list still works.`
    : "";
  const runChips = run
    ? [
        {
          id: "status",
          tone: runStatusTone,
          label: runStatusLabel,
          details: runStatusText(run),
        },
        runLocked && {
          id: "locked",
          tone: "warning",
          icon: MdLock,
          label: "Locked",
          details: lockText,
        },
        closing?.allFulfilled && {
          id: "fulfilled",
          tone: "success",
          icon: MdDoneAll,
          label: "All fulfilled",
          details: `${
            closing.stops.length === (run.stops ?? []).length
              ? "Every order on this run is fulfilled"
              : "Every order still to deliver is fulfilled"
          }, from the order page, so the run is still open. Complete run closes it with those stops fulfilled outside this run.`,
        },
        runOpen && {
            id: "location",
            tone: LOCATION_TONE[locationState] ?? "neutral",
            icon:
              locationState === "on" || locationState === "waiting"
                ? MdMyLocation
                : MdLocationOff,
            label: LOCATION_LABEL[locationState] ?? LOCATION_LABEL.off,
            details: (
              <span className="grid gap-[6px]">
                <span>{LOCATION_TEXT[locationState] ?? LOCATION_TEXT.off}</span>
                <label className="flex min-h-[36px] w-fit cursor-pointer items-center gap-[7px] font-semibold text-ui-text">
                  <input
                    type="checkbox"
                    checked={sharing}
                    onChange={toggleSharing}
                    className="h-[18px] w-[18px] accent-brand"
                  />
                  Share my location
                </label>
              </span>
            ),
          },
        driver?.seenAt && {
          id: "driver",
          tone: "neutral",
          icon: MdLocalShipping,
          label: [
            "Driver",
            agoText(driver.seenAt),
            driverNear ? metersText(driverNear.meters) : "",
          ]
            .filter(Boolean)
            .join(" · "),
          details: `Driver seen ${agoText(driver.seenAt)}${nearText ? `, ${nearText}` : ""}${driver.accuracy ? `, give or take ${metersText(driver.accuracy)}` : ""}.`,
        },
      ].filter(Boolean)
    : [];
  const openChip = runChips.find((chip) => chip.id === chipOpen);
  const runPhone = driverPhone(run?.driver);

  const copyLinks = () => {
    const text = links
      .map((link) =>
        links.length === 1 ? link.url : `${link.label}: ${link.url}`,
      )
      .join("\n");
    Promise.resolve()
      .then(() => navigator.clipboard.writeText(text))
      .then(
        () => notify.success("Links copied"),
        () => notify.error("Couldn't copy the links"),
      );
  };

  // The delivery list's props, for the list and the driver view's stop.
  const deliveryListProps = {
    stops,
    run: {
      routeName,
      startName: start?.name,
      date: run?.run_date,
    },
    statuses,
    canFulfil,
    onChanged: handleChanged,
    busy,
    onBusy: markBusy,
    onFresh: handleFresh,
    onOpenOrder: openOrder,
    notify,
    picks: deliveryPicks,
    ...(run && {
      onOutcome: handleOutcome,
      runOutcomes,
      spots,
      locked: !runOpen
        ? `This run is ${runStatusLabel.toLowerCase()}: nothing more to mark on it.`
        : canDriveRuns
          ? ""
          : NO_DRIVE_TEXT,
    }),
  };
  // The stops done on the run: recorded, or their orders fulfilled.
  const doneIds = new Set([
    ...(run?.stops ?? [])
      .filter((stop) => stop.outcome && stop.outcome !== "pending")
      .map((stop) => stop.order_id),
    ...stops
      .filter((order) => Number(order?.order_status_id) === FULFILLED_STATUS)
      .map((order) => order.id),
  ]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[1300] flex items-end justify-center bg-black/40 pt-[10px] sm:items-start sm:p-[14px]">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="routeSheetTitle"
        className="flex max-h-[calc(100dvh-10px)] w-full max-w-[1100px] flex-col overflow-hidden rounded-t-[16px] border-[1px] border-b-0 border-ui-border bg-ui-page font-manrope text-[12px] text-ui-text shadow-ui-md sm:max-h-[calc(100dvh-28px)] sm:rounded-[11px] sm:border-b-[1px]"
      >
        <header className="flex shrink-0 items-center gap-[11px] border-b-[1px] border-ui-border bg-ui-surface px-[14px] py-[12px]">
          <span className="grid h-[34px] w-[34px] shrink-0 place-items-center rounded-[8px] bg-brand-light text-brand">
            <MdOutlineRoute className="h-[18px] w-[18px]" />
          </span>
          <div className="grid min-w-[0px] flex-1 gap-[2px]">
            {/* Phones: the run or route only (the tab below names the view);
                screen readers still hear the view's name. */}
            <h2
              id="routeSheetTitle"
              className="m-[0px] font-jakarta text-[15px] font-extrabold leading-[1.25] tracking-[-0.01em] text-ui-text"
            >
              <span
                className={
                  run || routeName ? "sr-only sm:not-sr-only" : undefined
                }
              >
                {VIEWS.find(([id]) => id === view)[1]}
                {run || routeName ? " · " : ""}
              </span>
              {run ? runTitle(run) : routeName || ""}
            </h2>
            {/* Below lg it wraps: the start location must stay readable. */}
            <span className="font-manrope text-[12px] font-medium leading-[1.35] text-ui-text-muted lg:truncate lg:text-[11px]">
              {loading
                ? "Loading the orders…"
                : tooMany
                  ? `${plural(orderIds.length, "order")} ticked`
                  : view === "pick"
                    ? `${plural(list.toPick.length, "product")} to pick for ${plural(stops.length, "stop")}${start ? ` · from ${start.name}` : ""}`
                    : view === "delivery"
                      ? `${plural(stops.length, "stop")} · mark each one as you go`
                      : `${summary}${start ? ` · from ${start.name}` : ""} · stops in delivery order`}
            </span>
          </div>
          <button
            ref={closeRef}
            type="button"
            aria-label="Close"
            onClick={onClose}
            disabled={busy}
            className="grid h-[40px] w-[40px] shrink-0 place-items-center rounded-[8px] text-ui-text-subtle hover:bg-ui-surface-sunken hover:text-ui-text disabled:cursor-not-allowed disabled:opacity-40 lg:h-[30px] lg:w-[30px] [@media(pointer:coarse)]:h-[40px] [@media(pointer:coarse)]:w-[40px]"
          >
            <IoClose className="h-[18px] w-[18px]" />
          </button>
        </header>
        <div
          role="tablist"
          aria-label="Route sheet views"
          className="flex shrink-0 gap-[4px] overflow-x-auto border-b-[1px] border-ui-border bg-ui-surface px-[10px] py-[8px] md:px-[14px]"
        >
          {VIEWS.map(([id, label, Icon]) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={view === id}
              disabled={loading || !stops.length || busy}
              onClick={() => setView(id)}
              className={`flex shrink-0 items-center gap-[6px] rounded-full border-[1px] px-[10px] py-[5px] text-[12px] transition-colors disabled:opacity-40 md:px-[12px] [@media(max-width:399px)]:px-[9px] ${TAP} ${
                view === id
                  ? "border-brand bg-brand-light font-semibold text-brand"
                  : "border-transparent text-ui-text-muted hover:border-ui-border hover:text-ui-text"
              }`}
            >
              {/* The narrowest phones drop the icons so all three tabs fit. */}
              <Icon className="h-[14px] w-[14px] [@media(max-width:399px)]:hidden" />
              {label}
            </button>
          ))}
        </div>

        {/* Only the body scrolls; its panels keep their full height. */}
        <div className="flex min-h-[0px] flex-1 flex-col gap-[10px] overflow-auto p-[10px] [&>*]:shrink-0">
          {run && !loading && (
            <section
              aria-label="Run"
              className={`${PANEL} flex flex-wrap items-center gap-x-[12px] gap-y-[8px] p-[12px] print:hidden`}
            >
              {runChips.map((chip) => (
                <InfoChip
                  key={chip.id}
                  tone={chip.tone}
                  icon={chip.icon}
                  label={chip.label}
                  title={
                    typeof chip.details === "string" ? chip.details : undefined
                  }
                  open={chipOpen === chip.id}
                  onToggle={() => toggleChip(chip.id)}
                  panelId="runChipDetails"
                />
              ))}
              {openChip && (
                <ChipDetails
                  id="runChipDetails"
                  label={openChip.label}
                  tone={openChip.tone}
                >
                  {openChip.details}
                </ChipDetails>
              )}
              {canEditRun ? (
                // Phones: the date and driver share a row of their own, each
                // name above its field; from md up they sit in the bar.
                <div className="flex w-full gap-[8px] md:contents">
                  <label className="grid min-w-[0px] flex-1 gap-[3px] md:flex md:flex-initial md:items-center md:gap-[6px]">
                    <span className="text-[10px] font-semibold uppercase tracking-[0.06em] text-ui-text-muted">
                      Date
                    </span>
                    <input
                      type="date"
                      value={String(run.run_date ?? "").slice(0, 10)}
                      disabled={busy}
                      onChange={(event) =>
                        event.target.value &&
                        changeRun(
                          { run_date: event.target.value },
                          "Couldn't save the date. Try again.",
                        )
                      }
                      className={`w-full rounded-[6px] border-[1px] border-ui-border-strong bg-ui-surface px-[8px] py-[4px] text-ui-text md:w-auto ${FIELD_SIZE}`}
                    />
                  </label>
                  <label className="grid min-w-[0px] flex-1 gap-[3px] md:flex md:flex-initial md:items-center md:gap-[6px]">
                    <span className="text-[10px] font-semibold uppercase tracking-[0.06em] text-ui-text-muted">
                      Driver
                    </span>
                    <select
                      value={run.driver_id ? String(run.driver_id) : ""}
                      disabled={busy}
                      onChange={(event) =>
                        event.target.value === NEW_DRIVER
                          ? setAddingDriver(true)
                          : changeRun(
                              {
                                driver_id: event.target.value
                                  ? Number(event.target.value)
                                  : null,
                              },
                              "Couldn't save the driver. Try again.",
                            )
                      }
                      className={`w-full min-w-[0px] max-w-full rounded-[6px] border-[1px] border-ui-border-strong bg-ui-surface px-[8px] py-[4px] text-ui-text md:w-auto ${FIELD_SIZE}`}
                    >
                      <option value="">No driver yet</option>
                      {run.driver_id &&
                        !drivers.some(
                          (driver) => driver.id === run.driver_id,
                        ) && (
                          <option value={String(run.driver_id)}>
                            {driverName(run.driver) || `User #${run.driver_id}`}
                          </option>
                        )}
                      {drivers.map((driver) => (
                        <option key={driver.id} value={String(driver.id)}>
                          {driverName(driver) || `User #${driver.id}`}
                          {driver.role === "Driver" ? " · Driver" : ""}
                        </option>
                      ))}
                      <option value={NEW_DRIVER}>+ New driver…</option>
                    </select>
                  </label>
                </div>
              ) : (
                <span className="text-[12px] text-ui-text-muted">
                  {[runDay(run.run_date), driverName(run.driver) || "No driver"]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
              )}
              {runPhone && (
                <a
                  href={telHref(runPhone)}
                  className={`flex items-center gap-[4px] text-[12px] font-semibold text-brand hover:underline ${TAP}`}
                >
                  <MdCall className="h-[13px] w-[13px]" />
                  Call driver
                </a>
              )}
              {onShowOrders && (
                <button
                  type="button"
                  onClick={() => onShowOrders(run)}
                  disabled={busy}
                  title="Close the sheet and list this run's orders, to filter them by status"
                  className={`flex items-center gap-[4px] border-0 bg-transparent p-[0px] text-[12px] font-semibold text-brand hover:underline disabled:opacity-50 ${TAP}`}
                >
                  <MdFilterList className="h-[13px] w-[13px]" />
                  Show in orders list
                </button>
              )}
              {(canEditRun || canCompleteRun) && (
                <div className="ml-auto flex flex-wrap items-center gap-[8px]">
                  {closing && canCompleteRun && (
                    <BoltButton
                      type="button"
                      size="sm"
                      variant={closing.allFulfilled ? undefined : "outline"}
                      icon={MdDoneAll}
                      label={
                        closing.allFulfilled ? "Complete run" : "Close run"
                      }
                      title={
                        closing.allFulfilled
                          ? "Record the stops still to do as fulfilled outside this run (their orders are fulfilled) and close the run"
                          : "Record the stops still to do from their orders (fulfilled outside this run, or not delivered) and close the run"
                      }
                      onClick={() => setCompleting({ error: "" })}
                      disabled={busy}
                      className={TAP_BUTTON}
                    />
                  )}
                  {canEditRun && (
                    <>
                      <BoltButton
                        type="button"
                        variant="outline"
                        size="sm"
                        icon={runLocked ? MdLockOpen : MdLock}
                        label={runLocked ? "Unlock run" : "Lock run"}
                        title={
                          runLocked
                            ? "Let the stops change again"
                            : "Keep the stops as they are until it's unlocked"
                        }
                        onClick={toggleLock}
                        disabled={busy}
                        className={TAP_BUTTON}
                      />
                      <BoltButton
                        type="button"
                        variant="outline"
                        size="sm"
                        label="Cancel run"
                        title={
                          runLocked
                            ? "Unlock the run before cancelling it"
                            : undefined
                        }
                        onClick={openCancel}
                        disabled={busy || runLocked}
                        className={TAP_BUTTON}
                      />
                    </>
                  )}
                </div>
              )}
              {canEditRun && addingDriver && (
                <NewDriverForm
                  className="basis-full"
                  onCancel={() => setAddingDriver(false)}
                  onCreated={(driver) => {
                    setDrivers((list) => [
                      ...list.filter((entry) => entry.id !== driver.id),
                      driver,
                    ]);
                    setAddingDriver(false);
                    changeRun(
                      { driver_id: driver.id },
                      "Couldn't save the driver. Try again.",
                    );
                  }}
                />
              )}
              {run.notes && (
                <p className="m-[0px] basis-full text-[12px] text-ui-text-muted">
                  {run.notes}
                </p>
              )}
              {/* Pop-ups over the sheet, each shown only while open. */}
              {completing && closing && (
                <CompleteRunDialog
                  runName={runTitle(run)}
                  fulfilled={closing.fulfilled}
                  notDelivered={closing.notDelivered}
                  busy={busy}
                  error={completing.error}
                  onConfirm={confirmComplete}
                  onClose={() => setCompleting(null)}
                />
              )}
              {cancelling && (
                <CancelRunDialog
                  runName={runTitle(run)}
                  stopCount={(run.stops ?? []).length}
                  recorded={recordedStops(run)}
                  underWay={cancelling.underWay || recordedStops(run) > 0}
                  blocked={
                    runLocked
                      ? "Unlock the run before cancelling it."
                      : runOpen
                        ? ""
                        : `This run is ${runStatusLabel.toLowerCase()}.`
                  }
                  busy={busy}
                  error={cancelling.error}
                  onConfirm={confirmCancel}
                  onClose={() => setCancelling(null)}
                />
              )}
              {moving && (
                <MoveStopsDialog
                  run={run}
                  orders={moving}
                  drivers={drivers}
                  locations={locations}
                  startId={startId}
                  blocked={
                    runLocked
                      ? `${LOCKED_HINT}.`
                      : runOpen
                        ? ""
                        : `This run is ${runStatusLabel.toLowerCase()}.`
                  }
                  busy={busy}
                  onMove={moveTicked}
                  onClose={() => setMoving(null)}
                />
              )}
            </section>
          )}
          {loading ? (
            <div className="grid min-h-[200px] place-items-center">
              <CircularLoader size={24} />
            </div>
          ) : runFailed ? (
            <p className="m-[0px] rounded-[7px] border-[1px] border-state-danger-border bg-state-danger-bg p-[12px] text-[12.5px] text-state-danger-text">
              Couldn't load this run. Close and open it again.
            </p>
          ) : tooMany ? (
            <p className="m-[0px] rounded-[7px] border-[1px] border-state-warning-border bg-state-warning-bg p-[12px] text-[12.5px] text-state-warning-text">
              A route sheet takes up to {MAX_ORDERS} orders. Untick some and
              open it again.
            </p>
          ) : view === "delivery" ? (
            <>
              {run && runOpen && (
                <button
                  type="button"
                  onClick={() => setDriverOpen(true)}
                  className="flex min-h-[44px] items-center justify-center gap-[8px] rounded-[8px] border-[1px] border-brand bg-brand-light px-[12px] text-[13.5px] font-bold text-brand transition-colors hover:bg-brand hover:text-white print:hidden"
                >
                  <MdNavigation
                    aria-hidden="true"
                    className="h-[18px] w-[18px]"
                  />
                  Driver view: the map and the next stop
                </button>
              )}
              {driverOpen && run && runOpen && (
                <DriverView
                  title={runTitle(run)}
                  stops={stops}
                  start={start}
                  places={places}
                  points={drivePoints}
                  done={doneIds}
                  fix={where.fix}
                  location={sharing ? where.state : "off"}
                  onShare={sharing ? undefined : toggleSharing}
                  spots={spots}
                  renderStop={(orderId) => (
                    <DeliveryList
                      {...deliveryListProps}
                      onOpenOrder={undefined}
                      only={orderId}
                    />
                  )}
                  onClose={() => setDriverOpen(false)}
                />
              )}
              {run && runOpen && unrecordedCount > 0 && (
                <div
                  role="alert"
                  className="flex flex-wrap items-center gap-[8px] rounded-[7px] border-[1px] border-state-warning-border bg-state-warning-bg px-[12px] py-[8px] text-[12px] text-state-warning-text"
                >
                  <span className="flex-[1_1_220px]">
                    {plural(unrecordedCount, "stop")} saved on the order but not
                    yet on this run.
                  </span>
                  <button
                    type="button"
                    onClick={recordAgain}
                    disabled={busy}
                    className={`border-0 bg-transparent p-0 font-semibold text-brand hover:underline disabled:cursor-not-allowed disabled:opacity-40 ${TAP}`}
                  >
                    Record on the run
                  </button>
                </div>
              )}
              <DeliveryList {...deliveryListProps} />
            </>
          ) : view === "pick" ? (
            <section>
              <div className="hidden pb-[8px] print:block">
                <h1 className="m-[0px] text-[16px] font-bold">
                  Pick list{routeName ? ` · ${routeName}` : ""}
                </h1>
                <p className="m-[0px] text-[12px]">
                  {summary}
                  {start ? ` · from ${start.name}` : ""}
                </p>
              </div>
              <PickList
                list={list}
                picks={shownPicks}
                onPicked={markPicked}
                onMissing={markMissing}
                formatLb={formatLb}
              />
            </section>
          ) : (
            <>
              {missing.ids.length > 0 && (
                <div className="flex flex-wrap items-center gap-[8px] rounded-[7px] border-[1px] border-state-danger-border bg-state-danger-bg px-[12px] py-[8px] text-[12px] text-state-danger-text">
                  <span className="min-w-[0px] flex-[1_1_240px]">
                    {missing.cause === "failed"
                      ? `${plural(missing.ids.length, "order")} couldn't be loaded and ${missing.ids.length === 1 ? "isn't" : "aren't"} on this sheet. Close it and open it again.`
                      : `${missing.ids.map((id) => `#${id}`).join(", ")} ${missing.ids.length === 1 ? "isn't" : "aren't"} on this sheet: deleted, or not yours to see.`}
                  </span>
                  {removableMissing.length > 0 && (
                    <button
                      type="button"
                      onClick={takeOffMissing}
                      disabled={busy || runLocked}
                      title={runLocked ? LOCKED_HINT : undefined}
                      className={`${LINK_BTN} ${runLocked ? "disabled:cursor-not-allowed" : "disabled:cursor-wait"} disabled:opacity-50`}
                    >
                      {removableMissing.length === missing.ids.length
                        ? `Take ${removableMissing.length === 1 ? "it" : "them"} off the run`
                        : `Take ${removableMissing.map((id) => `#${id}`).join(", ")} off the run`}
                    </button>
                  )}
                </div>
              )}

              {/* One column that can't grow past the panel: a long location
                  name stays inside it instead of running off the edge. */}
              <section
                className={`${PANEL} grid grid-cols-1 gap-[10px] p-[12px]`}
              >
                <div className="flex flex-wrap items-center gap-[8px]">
                  <label
                    htmlFor="routeSheetStart"
                    className="text-[10px] font-semibold uppercase tracking-[0.06em] text-ui-text-muted"
                  >
                    Start and end at
                  </label>
                  {locations.length ? (
                    // Phones: the full width, under its name.
                    <select
                      id="routeSheetStart"
                      value={startId ?? ""}
                      onChange={(event) => changeStart(event.target.value)}
                      disabled={run ? !canEditRun || busy : false}
                      className={`w-full min-w-[0px] max-w-full rounded-[6px] border-[1px] border-ui-border-strong bg-ui-surface px-[8px] py-[5px] text-ui-text md:w-auto ${FIELD_SIZE}`}
                    >
                      {locations.map((location) => (
                        <option key={location.id} value={String(location.id)}>
                          {location.name}
                          {location.address ? ` — ${location.address}` : ""}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span className="text-[12px] text-ui-text-muted">
                      No location: the run starts where the driver is.
                    </span>
                  )}
                  {canEditRun && (
                    <div className="flex w-full flex-wrap items-center gap-[8px] md:ml-auto md:w-auto">
                      <button
                        type="button"
                        onClick={sortStops}
                        disabled={busy || runLocked || stops.length < 2}
                        aria-busy={sorting}
                        title={
                          runLocked
                            ? LOCKED_HINT
                            : "Sort the stops still to deliver for the shortest drive from the start and back"
                        }
                        className={`${LINK_BTN} ${STRETCH} ${busy ? "disabled:cursor-wait" : "disabled:cursor-not-allowed"} disabled:opacity-50`}
                      >
                        <MdOutlineRoute className="h-[13px] w-[13px] shrink-0" />
                        {sorting ? "Sorting…" : "Best order"}
                      </button>
                      <button
                        type="button"
                        onClick={reverseStops}
                        disabled={busy || runLocked || stops.length < 2}
                        aria-busy={reversing}
                        title={
                          runLocked
                            ? LOCKED_HINT
                            : "Turn the stops still to deliver the other way round"
                        }
                        className={`${LINK_BTN} ${STRETCH} ${busy ? "disabled:cursor-wait" : "disabled:cursor-not-allowed"} disabled:opacity-50`}
                      >
                        <MdSwapVert className="h-[13px] w-[13px] shrink-0" />
                        {reversing ? "Reversing…" : "Reverse order"}
                      </button>
                    </div>
                  )}
                </div>
                {links.length > 0 && (
                  <div className="flex flex-wrap items-center gap-[8px]">
                    {links.map((link) => (
                      <a
                        key={link.url}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`${LINK_BTN} ${STRETCH}`}
                      >
                        <MdOpenInNew className="h-[13px] w-[13px] shrink-0" />
                        {links.length === 1
                          ? "Open in Google Maps"
                          : `${link.label} in Google Maps`}
                      </a>
                    ))}
                    <button
                      type="button"
                      onClick={copyLinks}
                      className={`${LINK_BTN} ${STRETCH}`}
                    >
                      <MdContentCopy className="h-[13px] w-[13px] shrink-0" />
                      {links.length === 1 ? "Copy link" : "Copy links"}
                    </button>
                    {links.length > 1 && (
                      <span className="text-[11px] text-ui-text-subtle">
                        Google Maps takes 9 stops per link, so the run is in{" "}
                        {links.length} parts.
                      </span>
                    )}
                  </div>
                )}
                {driveState !== "idle" && (
                  <p
                    aria-live="polite"
                    className="m-[0px] flex flex-wrap items-center gap-[6px] text-[12px] text-ui-text-muted"
                  >
                    <MdDirectionsCar className="h-[15px] w-[15px] shrink-0 text-ui-text-subtle" />
                    {driveState === "working" ? (
                      "Working out the drive…"
                    ) : driveState === "failed" ? (
                      "Google Maps couldn't work out the drive for these stops."
                    ) : (
                      <span>
                        <span className="font-semibold text-ui-text">
                          Drive: {driveText(driveTotals)}
                        </span>
                        {start
                          ? `, from ${start.name} and back`
                          : ", first stop to last"}
                      </span>
                    )}
                  </p>
                )}
              </section>

              {/* Numbered stops on Google's map, with Full screen; Google's
                  embedded map when that can't show (see RouteSheetMap). */}
              <RouteSheetMap
                stops={stops}
                start={start}
                title={run ? runTitle(run) : routeName}
                driveTotals={driveTotals}
                spots={spots}
                driver={driver}
                candidates={mapCandidates}
                onAddStop={canChangeStops ? addStop : undefined}
                removableIds={removableIds}
                onRemoveStop={canChangeStops ? removeStop : undefined}
                busy={busy}
              />

              {/* From md up the stops are a table in a panel. On phones each
                  is a card, under the strips: a column, not a grid, so the
                  ticked bar can stick to the top while the cards scroll. */}
              <section
                className={
                  wide ? `${PANEL} overflow-x-auto` : "flex flex-col gap-[8px]"
                }
              >
                <div className="hidden px-[12px] pt-[12px] print:block">
                  <h1 className="m-[0px] text-[16px] font-bold">
                    Route sheet{routeName ? ` · ${routeName}` : ""}
                  </h1>
                  <p className="m-[0px] text-[12px]">
                    {summary}
                    {start ? ` · from ${start.name}, ${start.address}` : ""}
                  </p>
                </div>
                {tickedIds.length > 0 && (
                  <div
                    className={
                      wide
                        ? "flex flex-wrap items-center gap-[8px] border-b-[1px] border-ui-border bg-brand-light px-[12px] py-[7px] print:hidden"
                        : // Phones: Move stays in reach however far down the
                          // ticked card is.
                          "sticky top-0 z-[2] flex flex-wrap items-center gap-[8px] rounded-[7px] border-[1px] border-brand bg-brand-light px-[12px] py-[6px] shadow-ui-sm print:hidden"
                    }
                  >
                    <span className="text-[12px] font-semibold text-brand">
                      {plural(tickedIds.length, "stop")} ticked
                    </span>
                    <button
                      type="button"
                      onClick={() => setMoving(tickedOrders)}
                      disabled={busy}
                      className={`${LINK_BTN} disabled:cursor-wait disabled:opacity-50`}
                    >
                      <MdCallSplit className="h-[13px] w-[13px] shrink-0" />
                      Move {plural(tickedIds.length, "stop")}…
                    </button>
                    <button
                      type="button"
                      onClick={() => setTicked([])}
                      className={`ml-auto border-0 bg-transparent p-[0px] text-[12px] font-semibold text-ui-text-muted hover:text-ui-text ${TAP}`}
                    >
                      Untick all
                    </button>
                  </div>
                )}
                {stops.length > 0 && (
                  <div
                    className={
                      wide
                        ? "flex flex-wrap items-center gap-[6px] border-b-[1px] border-ui-border bg-ui-surface-sunken px-[12px] py-[7px]"
                        : "flex flex-wrap items-center gap-[6px] rounded-[7px] border-[1px] border-ui-border bg-ui-surface-sunken px-[12px] py-[8px]"
                    }
                  >
                    <SheetChip tone="neutral">
                      {counts.toPick} pcs to pick
                    </SheetChip>
                    {counts.packing > 0 && (
                      <SheetChip tone="info">
                        {counts.packing} pcs being packed
                      </SheetChip>
                    )}
                    {counts.fulfilled > 0 && (
                      <SheetChip tone="success">
                        {plural(counts.fulfilled, "line")} fulfilled
                      </SheetChip>
                    )}
                    {counts.unlinked > 0 && (
                      <SheetChip tone="danger">
                        {plural(counts.unlinked, "line")} not in Products
                      </SheetChip>
                    )}
                    <span
                      className={
                        wide
                          ? "ml-auto text-[11px] text-ui-text-muted"
                          : "basis-full text-[12px] text-ui-text-muted"
                      }
                    >
                      Fulfilled lines are struck through: don't pick them again.
                    </span>
                  </div>
                )}
                {wide ? (
                  // Words stay whole here: the app's `* { overflow-wrap:
                  // anywhere }` would let the columns shrink to a letter wide.
                  // A tablet in portrait (768px) fits it without scrolling
                  // sideways; it scrolls inside the panel only if a stop's
                  // words can't fit.
                  <table className="w-full min-w-[680px] border-collapse text-left text-[12px] [overflow-wrap:break-word] lg:min-w-[760px] [&_*]:[overflow-wrap:break-word]">
                    <thead>
                      <tr className="whitespace-nowrap border-b-[1px] border-ui-border bg-ui-surface-sunken text-[10.5px] uppercase tracking-[0.04em] text-ui-text-muted">
                        {canEditRun && (
                          <th className="w-[1%] px-[10px] py-[8px] print:hidden">
                            {tickAllBox(
                              "h-[15px] w-[15px] align-middle accent-brand",
                            )}
                          </th>
                        )}
                        <th className="px-[10px] py-[8px] font-bold">#</th>
                        <th className="px-[10px] py-[8px] font-bold">Order</th>
                        <th className="px-[10px] py-[8px] font-bold">
                          Customer
                        </th>
                        <th className="px-[10px] py-[8px] font-bold">
                          Address
                        </th>
                        <th className="px-[10px] py-[8px] font-bold">Items</th>
                        <th className="px-[10px] py-[8px] text-right font-bold">
                          Pcs
                        </th>
                        <th className="px-[10px] py-[8px] text-right font-bold">
                          Weight
                        </th>
                        <th className="px-[10px] py-[8px] print:hidden">
                          <span className="sr-only">Actions</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {stops.map((order, index) => (
                        <StopRow
                          key={order.id}
                          parts={stopParts({
                            order,
                            index,
                            lines: checked[index],
                            sheet: stopSheet,
                          })}
                        />
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <>
                    {canEditRun && (
                      <label className="flex min-h-[44px] cursor-pointer items-center gap-[10px] rounded-[7px] border-[1px] border-ui-border bg-ui-surface px-[12px] text-[12.5px] font-semibold text-ui-text-muted print:hidden">
                        {tickAllBox("h-[20px] w-[20px] shrink-0 accent-brand")}
                        Tick every stop still to deliver
                      </label>
                    )}
                    <ol
                      aria-label="Stops"
                      className="m-[0px] grid list-none gap-[8px] p-[0px]"
                    >
                      {stops.map((order, index) => (
                        <StopCard
                          key={order.id}
                          parts={stopParts({
                            order,
                            index,
                            lines: checked[index],
                            sheet: stopSheet,
                            look: "card",
                          })}
                        />
                      ))}
                    </ol>
                  </>
                )}
              </section>

              {noAddress.length > 0 && (
                <p className="m-[0px] text-[11px] text-ui-text-muted">
                  {noAddress.map((order) => `#${order.id}`).join(", ")}{" "}
                  {noAddress.length === 1 ? "has" : "have"} no delivery address,
                  so {noAddress.length === 1 ? "it isn't" : "they aren't"} in
                  the links or on the map.
                </p>
              )}
            </>
          )}
        </div>

        <footer className="flex shrink-0 flex-wrap items-center gap-[8px] border-t-[1px] border-ui-border bg-ui-surface px-[14px] pb-[calc(12px+env(safe-area-inset-bottom,0px))] pt-[10px] sm:py-[10px]">
          {/* Phones keep the room for the list: the buttons only. */}
          <span className="hidden flex-[1_1_180px] font-manrope text-[11px] font-medium text-ui-text-muted sm:block">
            {loading || tooMany
              ? ""
              : view === "pick"
                ? "Tick each product as it's picked. Missing? flags it, and the route sheet shows it too."
                : view === "delivery"
                  ? "Each action saves to the order straight away, as the order view does."
                  : `${summary}. The driver can still change the order on the way.`}
          </span>
          <div className="ml-auto flex gap-[8px]">
            <BoltButton
              type="button"
              variant="outline"
              size="sm"
              label="Close"
              onClick={onClose}
              disabled={busy}
              className={TAP_BUTTON}
            />
            <PrintMenu
              title={`Print the ${VIEW_LABEL[view] ?? "route sheet"}`}
              onMake={makePrint}
              disabled={loading || tooMany || !stops.length}
              buttonClassName={TAP_BUTTON}
            />
          </div>
        </footer>
      </div>
    </div>,
    document.body,
  );
};

export default RouteSheetModal;
